import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Vote, Globe, ArrowRight } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import QuestionCard from "@/components/QuestionCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Home() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [stats, setStats] = useState(null);
  const [country, setCountry] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/countries").then(({ data }) => setCountries(data));
    api.get("/stats").then(({ data }) => setStats(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { status: "active" };
    if (country !== "all") params.country = country;
    api.get("/questions", { params }).then(({ data }) => setQuestions(data)).finally(() => setLoading(false));
  }, [country]);

  const cName = (c) => (lang === "it" ? c.name_it : c.name_en);

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border bg-primary text-primary-foreground">
        <div className="absolute inset-0 civic-grid opacity-10" />
        <div className="relative max-w-6xl mx-auto px-4 py-16 sm:py-24">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-primary-foreground/70">{t("app.tagline")}</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter max-w-3xl">{t("home.hero.title")}</h1>
          <p className="mt-5 text-base sm:text-lg text-primary-foreground/85 max-w-2xl leading-relaxed">{t("home.hero.subtitle")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="hero-cta" size="lg" variant="secondary" onClick={() => document.getElementById("active")?.scrollIntoView({ behavior: "smooth" })}>
              <Vote className="h-4 w-4 mr-2" />{t("home.hero.cta")}
            </Button>
            <Button data-testid="hero-cta2" size="lg" variant="outline" className="bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary" onClick={() => nav("/registry")}>
              <ShieldCheck className="h-4 w-4 mr-2" />{t("nav.registry")}
            </Button>
          </div>
        </div>
      </section>

      {stats && (
        <section className="max-w-6xl mx-auto px-4 -mt-8 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { k: "home.stats.questions", v: stats.questions },
              { k: "home.stats.votes", v: stats.votes },
              { k: "home.stats.citizens", v: stats.verified_citizens },
              { k: "home.stats.countries", v: stats.countries },
            ].map((s) => (
              <div key={s.k} data-testid={`stat-${s.k}`} className="rounded-lg border border-border bg-card p-5 shadow-sm">
                <div className="font-heading text-3xl font-black text-primary">{s.v}</div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{t(s.k)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section id="active" className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Vote className="h-6 w-6 text-primary" />{t("home.active")}</h2>
          <div className="w-full sm:w-64">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="home-country-filter"><Globe className="h-4 w-4 mr-2 text-muted-foreground" /><SelectValue placeholder={t("home.filter.country")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                {countries.map((c) => <SelectItem key={c.code} value={c.code}>{cName(c)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <p className="text-muted-foreground">{t("common.loading")}</p>
        ) : questions.length === 0 ? (
          <p data-testid="home-empty" className="text-muted-foreground py-12 text-center">{t("home.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {questions.map((q, i) => <QuestionCard key={q.id} q={q} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
